// The characters allowed in a tag body (after the `#`) AND in the leading
// boundary check: letters (`\p{L}`), combining marks (`\p{M}`, so decomposed
// accents survive), numbers (`\p{N}`), underscore, plus `/` (hierarchy) and
// `-`. Written with Unicode property escapes on purpose: `\w` is ASCII-only in
// JavaScript (no `u` flag makes it Unicode) but Unicode-aware in Swift/ICU
// `NSRegularExpression`, so the two diverged on non-ASCII input (`#café`,
// `#日本語`, and `café#tag` boundary cases). `\p{…}` resolves identically under
// the JS `u` flag and ICU, so this is the single cross-platform definition.
// Mirrored byte-for-byte by `hashtagTokenCharacterClass` in Swift's
// `HashtagExtractor.swift`. Change here AND there, or neither.
export const HASHTAG_TOKEN_CLASS = '[\\p{L}\\p{M}\\p{N}_/-]'

// The body of a hashtag: either a single run of tag chars (`#work`, with an
// optional closing `#` that Bear also accepts: `#work#`), or several runs
// separated by spaces that MUST be closed by a `#` (`#my project#`). The
// closing hash is what disambiguates `#my project#` (one tag) from `#my project`
// (tag `my`, then the word "project"). Multi-word bodies use a lookahead for
// the closing `#` so the greedy space-separated run can only win when it is
// actually closed; otherwise the engine falls back to the single-word branch.
// Only plain spaces are allowed inside (no tabs / newlines).
//
// Mirrored byte-for-byte by `hashtagBodyPattern` in Swift's
// `HashtagExtractor.swift`. Change here AND there, or neither.
export const HASHTAG_BODY_PATTERN = `((?:${HASHTAG_TOKEN_CLASS}+(?: +${HASHTAG_TOKEN_CLASS}+)+(?=#))|${HASHTAG_TOKEN_CLASS}+)#?`

// A `#` only starts a tag when it isn't glued to a preceding tag-body char.
// The negative lookbehind rejects URL fragments (`example.com#frag`) and
// mid-word hashes (`foo#bar`) while still allowing a tag after whitespace, a
// newline, the start of the string, or punctuation like `[` (so `[#work]` link
// text still extracts). The `u` flag is required for the `\p{…}` escapes.
// Group 1 is the raw body (no `#` on either side); normalize it with
// `normalizeHashtagBody` before comparing or storing.
export const HASHTAG_REGEX = new RegExp(
  `(?<!${HASHTAG_TOKEN_CLASS})#${HASHTAG_BODY_PATTERN}`,
  'gu',
)

/**
 * Normalize a raw hashtag body (the text between the hashes) to its canonical
 * handle: lowercase, runs of spaces collapsed to one, slashes collapsed and
 * stripped of surrounding spaces, no leading/trailing slash or space.
 * `My   Project` → `my project`, `Work / My Project` → `work/my project`.
 * Returns '' when nothing survives (e.g. a slash-only body). `a / / b` → `a/b`.
 */
export function normalizeHashtagBody(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/ +/g, ' ')
    .replace(/ *\/+ */g, '/')
    .replace(/\/+/g, '/')
    .replace(/^\/|\/$/g, '')
    .trim()
}

/**
 * Normalize a tag name segment typed or sent by a client (rename dialog,
 * `POST /tags`, sync push): trimmed, lowercase, runs of spaces collapsed.
 * Mirrors what extraction stores for the same segment.
 */
export function normalizeTagName(name: string): string {
  return name.trim().toLowerCase().replace(/ +/g, ' ')
}

/**
 * Render a handle as it must be written in note content: `#work` for a
 * single-word handle, `#my project#` (closing hash) when any segment
 * contains a space. Mirrored by `hashtagText(for:)` in Swift.
 */
export function formatHashtag(handle: string): string {
  return handle.includes(' ') ? `#${handle}#` : `#${handle}`
}

/**
 * Strip fenced code blocks (```...```) and inline code (`...`) from markdown
 * so that hashtags/mentions inside code are not extracted.
 */
export function stripCode(md: string): string {
  // Fenced code blocks first (greedy across lines), then inline code
  return md.replace(/```[\s\S]*?```/g, '').replace(/`[^`]*`/g, '')
}

/**
 * Strip the destination (the `(...)` part) of markdown inline links and images
 * — e.g. `[References](#references)` or `![alt](path#frag)` — so a `#` inside a
 * link/image target is never extracted as a hashtag. The visible label between
 * the brackets is preserved, so a legitimate tag in link text is still found.
 */
export function stripLinkTargets(md: string): string {
  return md.replace(/(!?\[[^\]]*\])\([^)]*\)/g, '$1')
}

/**
 * Extract all unique hashtag names from a markdown string.
 * Returns lowercase tag names without the leading `#`.
 * Hierarchical tags like `#work/project` are kept as-is; multi-word tags
 * (`#my project#`) come back with their spaces (`my project`).
 * Hashtags inside code blocks or inline code are ignored.
 */
export function extractHashtags(content: string): string[] {
  const tags = new Set<string>()
  const cleaned = stripLinkTargets(stripCode(content))
  let match
  HASHTAG_REGEX.lastIndex = 0
  while ((match = HASHTAG_REGEX.exec(cleaned)) !== null) {
    const normalized = normalizeHashtagBody(match[1])
    if (normalized) tags.add(normalized)
  }
  return Array.from(tags)
}
